/**
 * One dossier: what they declared, what they sent, and the decision.
 *
 * The design's three columns, and they are three because they answer
 * different questions and the ADV moves between them — a stacked layout makes
 * them scroll to compare an ICE against the document that proves it.
 *
 * Four decisions about the review itself:
 *
 * **An upload is not a check.** Each document has a button a person presses,
 * and it can be un-pressed: an ADV who ticked the wrong line needs to undo it
 * without asking the applicant to send the file again.
 *
 * **The completeness gap is named, not summarised.** "2 / 3" tells the ADV
 * there is work; the missing kind tells them what to write in the e-mail.
 *
 * **Approving states its consequences before it happens.** It creates a
 * customer, invites a person and writes to the audit trail, and a button
 * whose effects are three modules away should say so.
 *
 * **The override is offered only to somebody who holds it.** The server
 * refuses it either way; rendering the checkbox for everybody would make it a
 * control that looks live and is not.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { EM_DASH, moment, money } from "@hardauto/core-i18n";
import { Field } from "@hardauto/core-forms";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  ErrorState,
  Input,
  LoadingState,
  Page,
  Select,
  StatusDot,
  Textarea,
  useToast,
} from "@hardauto/core-ui";

import { OnboardingApi, type AuthMethod, type RequestDetail, type SubmittedDocument } from "../api";
import { NAMESPACE } from "../i18n";
import { ONBOARDING_PATHS } from "../paths";
import "./onboarding.css";

const METHODS: readonly AuthMethod[] = ["otp", "password_mfa"];

export function RequestScreen({ client, id }: { client: ApiClient; id: string }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new OnboardingApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayDecide = useCan("onboarding.request.decide");
  const mayOverride = useCan("onboarding.request.override");

  const fetcher = useCallback(() => api.get(id), [api, id]);
  const request = useResource(fetcher);

  const [decision, setDecision] = useState({
    sales_rep_code: "",
    site_code: "",
    price_scale_code: "",
    credit_limit: "0.00",
    auth_method: "otp" as AuthMethod,
    allow_incomplete: false,
  });
  const [reason, setReason] = useState("");

  if (request.loading) return <LoadingState label={t("list.title")} />;
  if (!request.data) {
    return (
      <ErrorState title={t("error.load")} onRetry={request.reload} retryLabel={t("error.retry")} />
    );
  }

  const dossier = request.data;
  const rep = decision.sales_rep_code || dossier.sales_rep_code;

  return (
    <Page
      title={dossier.company_name}
      eyebrow={`${dossier.reference} · ${t(`status.${dossier.status}`)}`}
      back={<Link to={ONBOARDING_PATHS.requests}>{t("detail.back")}</Link>}
      actions={
        mayDecide && dossier.status === "submitted" ? (
          <Button
            variant="secondary"
            busy={action.isBusy("review")}
            busyLabel={t("public.submitting")}
            onClick={() =>
              void action.run("review", async () => {
                try {
                  await api.review(dossier.id);
                  request.reload();
                } catch {
                  toast.show(t("error.title"), { tone: "danger" });
                }
              })
            }
          >
            {t("detail.review")}
          </Button>
        ) : undefined
      }
    >
      {dossier.decided_at ? (
        <Alert
          tone={dossier.status === "approved" ? "success" : "warning"}
          title={`${t("detail.decidedOn")} ${moment(dossier.decided_at, i18n.language)}`}
        >
          {dossier.decision_reason ? (
            <p>
              {t("detail.decidedReason")}: {dossier.decision_reason}
            </p>
          ) : null}
          {dossier.client_id ? (
            <p>
              {t("detail.customer")}:{" "}
              <a href={`/clients/${dossier.client_id}`}>{t("detail.openCustomer")}</a>
            </p>
          ) : null}
        </Alert>
      ) : null}

      <div className="hardauto-dossier">
        <Card title={t("detail.declared")} description={t("detail.declaredHint")} compactTitle>
          <dl className="hardauto-declared">
            {[
              [t("public.companyName"), dossier.company_name],
              [t("public.legalForm"), dossier.legal_form],
              [t("public.taxId"), dossier.tax_id],
              [t("public.tradeRegister"), dossier.trade_register],
              [t("public.city"), dossier.city],
              [t("public.address"), dossier.address],
              [t("public.contactName"), dossier.contact_name],
              [t("public.contactEmail"), dossier.contact_email],
              [t("public.contactPhone"), dossier.contact_phone],
              [t("public.activity"), dossier.activity],
              [t("public.estimatedVolume"), dossier.estimated_volume],
              [t("public.desiredSite"), dossier.desired_site_code],
            ].map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value || EM_DASH}</dd>
              </div>
            ))}
          </dl>
          {dossier.tax_id && !dossier.ice_looks_valid ? (
            <Alert tone="warning" title={t("list.iceInvalid")} />
          ) : null}
        </Card>

        <Card title={t("detail.documents")} description={t("detail.documentsHint")} compactTitle>
          {dossier.completeness.is_complete ? (
            <Alert tone="success" title={t("detail.complete")} />
          ) : (
            <Alert
              tone="warning"
              title={dossier.completeness.summary}
              action={
                mayDecide ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    busy={action.isBusy("ask")}
                    busyLabel={t("public.submitting")}
                    onClick={() =>
                      void action.run("ask", async () => {
                        try {
                          await api.requestDocuments(dossier.id);
                          toast.show(t("detail.asked"));
                          request.reload();
                        } catch (error) {
                          toast.show(error instanceof Error ? error.message : t("error.title"), {
                            tone: "danger",
                          });
                        }
                      })
                    }
                  >
                    {t("detail.askDocuments")}
                  </Button>
                ) : undefined
              }
            >
              {dossier.completeness.missing.length > 0 ? (
                <p>
                  {t("detail.missing")}:{" "}
                  {dossier.completeness.missing.map((kind) => t(`document.${kind}`)).join(", ")}
                </p>
              ) : null}
              {dossier.completeness.unverified.length > 0 ? (
                <p>
                  {t("detail.unverified")}:{" "}
                  {dossier.completeness.unverified.map((kind) => t(`document.${kind}`)).join(", ")}
                </p>
              ) : null}
            </Alert>
          )}

          <div className="hardauto-pieces">
            {dossier.documents.length === 0 ? (
              <p className="hardauto-hint">{t("detail.noDocuments")}</p>
            ) : (
              dossier.documents.map((document) => (
                <Piece
                  key={document.id}
                  document={document}
                  dossier={dossier}
                  api={api}
                  onChanged={request.reload}
                  mayDecide={mayDecide}
                />
              ))
            )}
          </div>
        </Card>

        <Card title={t("detail.decision")} compactTitle>
          {dossier.decided_at || !mayDecide ? (
            <p className="hardauto-hint">{t("detail.consequences")}</p>
          ) : (
            <div className="hardauto-decision">
              <Field label={t("detail.rep")} hint={t("detail.repRequired")} required>
                {(props) => (
                  <Input
                    {...props}
                    value={rep}
                    onChange={(event) =>
                      setDecision({ ...decision, sales_rep_code: event.target.value })
                    }
                  />
                )}
              </Field>
              <Field label={t("detail.site")}>
                {(props) => (
                  <Input
                    {...props}
                    value={decision.site_code || dossier.desired_site_code}
                    onChange={(event) =>
                      setDecision({ ...decision, site_code: event.target.value })
                    }
                  />
                )}
              </Field>
              <Field label={t("detail.scale")}>
                {(props) => (
                  <Input
                    {...props}
                    value={decision.price_scale_code}
                    onChange={(event) =>
                      setDecision({ ...decision, price_scale_code: event.target.value })
                    }
                  />
                )}
              </Field>
              <Field label={t("detail.creditLimit")}>
                {(props) => (
                  <Input
                    {...props}
                    inputMode="decimal"
                    value={decision.credit_limit}
                    onChange={(event) =>
                      setDecision({ ...decision, credit_limit: event.target.value })
                    }
                  />
                )}
              </Field>
              <Field label={t("detail.authMethod")} hint={t(`auth.${decision.auth_method}Note`)}>
                {(props) => (
                  <Select
                    {...props}
                    value={decision.auth_method}
                    onChange={(event) =>
                      setDecision({
                        ...decision,
                        auth_method: event.target.value as AuthMethod,
                      })
                    }
                  >
                    {METHODS.map((method) => (
                      <option key={method} value={method}>
                        {t(`auth.${method}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {mayOverride ? (
                <>
                  <Checkbox
                    label={t("detail.allowIncomplete")}
                    checked={decision.allow_incomplete}
                    onChange={(event) =>
                      setDecision({ ...decision, allow_incomplete: event.target.checked })
                    }
                  />
                  <p className="hardauto-decision__note">{t("detail.allowIncompleteHint")}</p>
                </>
              ) : null}

              <div className="hardauto-decision__actions">
                <Button
                  variant="primary"
                  disabled={!rep.trim()}
                  busy={action.isBusy("approve")}
                  busyLabel={t("detail.approving")}
                  onClick={() =>
                    void action.run("approve", async () => {
                      try {
                        await api.approve(dossier.id, {
                          sales_rep_code: rep.trim(),
                          site_code: decision.site_code || dossier.desired_site_code,
                          price_scale_code: decision.price_scale_code,
                          credit_limit: decision.credit_limit,
                          auth_method: decision.auth_method,
                          allow_incomplete: decision.allow_incomplete,
                        });
                        toast.show(t("detail.approved"));
                        request.reload();
                      } catch (error) {
                        // The server's own sentence: it names the missing
                        // document, or the representative that does not
                        // exist. A generic toast would throw away the only
                        // thing the ADV needs.
                        toast.show(error instanceof Error ? error.message : t("error.title"), {
                          tone: "danger",
                        });
                      }
                    })
                  }
                >
                  {t("detail.approve")}
                </Button>
              </div>

              <p className="hardauto-decision__note">{t("detail.consequences")}</p>

              <Field label={t("detail.reason")} hint={t("detail.reasonHint")}>
                {(props) => (
                  <Textarea
                    {...props}
                    rows={2}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                )}
              </Field>
              <div className="hardauto-decision__actions">
                <Button
                  variant="secondary"
                  disabled={!reason.trim()}
                  busy={action.isBusy("reject")}
                  busyLabel={t("detail.rejecting")}
                  onClick={() =>
                    void action.run("reject", async () => {
                      try {
                        await api.reject(dossier.id, reason.trim());
                        toast.show(t("detail.rejected"));
                        request.reload();
                      } catch (error) {
                        toast.show(error instanceof Error ? error.message : t("error.title"), {
                          tone: "danger",
                        });
                      }
                    })
                  }
                >
                  {t("detail.reject")}
                </Button>
              </div>
            </div>
          )}

          {dossier.credit_limit && dossier.decided_at ? (
            <p className="hardauto-hint">
              {t("detail.creditLimit")}: {money(dossier.credit_limit, i18n.language)}
            </p>
          ) : null}
        </Card>
      </div>
    </Page>
  );
}

/**
 * One supporting document: its name, whether a person has checked it, and the
 * two things that can be done with it.
 *
 * Its own component so the check button's busy state belongs to the document
 * rather than to the page — three documents checked in a row should not grey
 * each other out.
 */
function Piece({
  document,
  dossier,
  api,
  onChanged,
  mayDecide,
}: {
  document: SubmittedDocument;
  dossier: RequestDetail;
  api: OnboardingApi;
  onChanged: () => void;
  mayDecide: boolean;
}) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();

  return (
    <article className="hardauto-piece">
      <div className="hardauto-piece__head">
        <strong>{t(`document.${document.kind}`)}</strong>
        <StatusDot
          label={document.is_verified ? t("detail.verified") : t("detail.unverified")}
          tone={document.is_verified ? "success" : "warning"}
        />
      </div>
      <div className="hardauto-piece__meta">
        {document.filename} · {document.size} o
        {document.verified_at ? ` · ${moment(document.verified_at, i18n.language)}` : ""}
      </div>
      <div className="hardauto-piece__actions">
        <Button
          variant="ghost"
          size="sm"
          busy={action.isBusy(`download:${document.id}`)}
          busyLabel={t("public.submitting")}
          onClick={() =>
            void action.run(`download:${document.id}`, async () => {
              try {
                const blob = await api.document(dossier.id, document.id);
                // Fetched with the session rather than linked: the route is
                // behind a bearer token, and an `<a href>` would download the
                // sign-in page.
                const url = URL.createObjectURL(blob);
                const link = window.document.createElement("a");
                link.href = url;
                link.download = document.filename;
                link.click();
                URL.revokeObjectURL(url);
              } catch {
                toast.show(t("error.title"), { tone: "danger" });
              }
            })
          }
        >
          {t("detail.download")}
        </Button>
        {mayDecide ? (
          <Button
            variant="ghost"
            size="sm"
            busy={action.isBusy(`verify:${document.id}`)}
            busyLabel={t("public.submitting")}
            onClick={() =>
              void action.run(`verify:${document.id}`, async () => {
                try {
                  await api.verify(dossier.id, document.id, !document.is_verified);
                  onChanged();
                } catch {
                  toast.show(t("error.title"), { tone: "danger" });
                }
              })
            }
          >
            {document.is_verified ? t("detail.unverify") : t("detail.verify")}
          </Button>
        ) : null}
      </div>
    </article>
  );
}
